import { useState } from "react";
import { SendStepsCommon, TriggerStepCreate } from "@dots-projects/types";
import { Controller, ControllerRenderProps, useForm } from "react-hook-form";
import produce from "immer";
import { saveDraft, updateStep } from "./reducer/actionCreators";
import { useTriggerEditorStateDispatch } from "./TriggerEditor";
import invariant from "ts-invariant";
import { StepFormWithCommonParts } from "./TriggerStepForm";
import { Alert, Checkbox } from "antd";
import { getAvailableChannelVariablesChannelsBySteps } from "libs/types/src/lib/getAvailableVariables";
import { canStepBeEphemeral } from "./reducer/triggerUtils";
import { FormLabel } from "./FormLabel";
import { CheckboxChangeEvent } from "antd/lib/checkbox";
import TriggerEditorChannelSelector from "./TriggerEditorChannelSelector";
import EmbedMessageInput from "./EmbedMessageInput";
import { Input } from "./Input";

/**
 * Renders the form for a "Send embed message" step. When the form is submitted, generate the step data and publish an update action to add a new step to the trigger flow.
 */
export function StepFormSendEmbedMessage({
  step,
}: {
  step: TriggerStepCreate;
}) {
  invariant(step.type === "send_message");

  const defaultValues = {
    name: step.name,
    message: step.configs.message,
    ephemeral: !!step.configs.ephemeral,
    channelId: step.configs.channelId,
    delayMinutes:
      typeof step.configs.delaySeconds === "number"
        ? step.configs.delaySeconds / 60
        : 0,
    delayed:
      typeof step.configs.delaySeconds === "number" &&
      step.configs.delaySeconds > 0,
  };

  const form = useForm({
    defaultValues,
    mode: "onChange",
  });
  const {
    handleSubmit,
    watch,
    control,
    setValue,
    formState: { errors },
    register,
  } = form;
  const { state } = useTriggerEditorStateDispatch();
  const { dispatch, onPersist } = useTriggerEditorStateDispatch();
  const availableChannels = getAvailableChannelVariablesChannelsBySteps(
    state.trigger.current
  );
  const isEphemeral = watch("ephemeral");
  const readOnly = !state.trigger.current.isDraft;
  const stepConfig = step.configs as SendStepsCommon;
  const [showEphemeralError, setShowEphemeralError] = useState(false);
  const [errorSelect, setErrorSelect] = useState(
    ["Select channel", "$channelId"].includes(stepConfig.channelId)
  );
  const stepCanBeEphemeral = canStepBeEphemeral(state.trigger.current, step);
  const showDelayInput = watch("delayed");

  const onSubmit = (data: any) => {
    // This uses the immer library to create a new copy of step (with updates)
    // without affecting the original step.
    const newStep = produce(step, (stepDraft) => {
      stepDraft.name = data.name;
      stepDraft.configs.ephemeral = data.ephemeral;
      stepDraft.configs.channelId = data.channelId;
      stepDraft.configs.message = data.message;
      stepDraft.configs.delaySeconds = data.delayMinutes
        ? data.delayMinutes * 60
        : 0;
    });
    dispatch(updateStep(newStep));
    dispatch(saveDraft(onPersist));
  };

  const onEphemeralChange = (
    e: CheckboxChangeEvent,
    formField: ControllerRenderProps<any, "ephemeral">
  ) => {
    if (!stepCanBeEphemeral) {
      e.target.checked = false;
      setShowEphemeralError(true);
    }
    formField.onChange(e.target.checked);
  };

  return (
    <StepFormWithCommonParts
      onSubmit={handleSubmit(onSubmit)}
      form={form}
      step={step}
    >
      <Controller
        control={control}
        name="ephemeral"
        // rules={{ validate: () => stepCanBeEphemeral }}
        render={({ field }) => (
          <>
            <Checkbox
              disabled={readOnly}
              checked={field.value}
              onChange={(value) => {
                onEphemeralChange(value, field);
              }}
            >
              <span className="text-gray-500">Only visible to the user</span>
            </Checkbox>
            {showEphemeralError && (
              <Alert
                description="Previous step has to be send_survey, send_multiple_select or trigger type is either a button click or slash command."
                type="error"
                closable
                onClose={() => setShowEphemeralError(false)}
              />
            )}
          </>
        )}
      />
      {!isEphemeral && (
        <Controller
          control={control}
          name="channelId"
          rules={{
            required: true,
            validate: (channelId) => {
              return (
                channelId &&
                channelId !== "Select channel" &&
                channelId !== "$channelId"
              );
            },
          }}
          render={({ field }) => (
            <TriggerEditorChannelSelector
              readOnly={readOnly}
              field={field}
              errorSelect={errorSelect}
              trigger={state.trigger.current}
              showRefreshButton
            />
          )}
        />
      )}
      <EmbedMessageInput form={form} showLabel />
      <Controller
        control={control}
        name="delayed"
        render={({ field }) => (
          <>
            <Checkbox
              disabled={readOnly}
              checked={field.value}
              onChange={(e) => {
                if (!e.target.checked) {
                  setValue("delayMinutes", 0);
                }
                field.onChange(e.target.checked);
              }}
            >
              <span className="text-gray-500">
                Check here to add a delay before sending this message
              </span>
            </Checkbox>
          </>
        )}
      />
      {showDelayInput && (
        <FormLabel label="Send messge after">
          <div className="flex w-full	items-end justify-items-center">
            <Input
              style={{ width: "100%" }}
              readOnly={readOnly}
              {...register("delayMinutes", {
                valueAsNumber: true,
                validate: (value) => value >= 0,
              })}
            />
            <p style={{ margin: "0 0 0 15px" }}> Minutes</p>
          </div>
          {errors["delayMinutes"] && (
            <p className="text-red-500 m-0">Minimum value is 0</p>
          )}
        </FormLabel>
      )}
    </StepFormWithCommonParts>
  );
}
